/**
 * Top-of-page hero banner for the salon booking widget.
 *
 * Renders the salon cover image with a dark gradient overlay, then layers
 * the salon identity (logo, name, rating, address, hours) on top — same
 * visual hierarchy as the alteg.io / Booksy widgets it competes with.
 */

import { useTranslation } from "react-i18next";
import { pickLocalized } from "./salonTheme";

export function SalonHero({ palette, salon, lang }) {
  const { t } = useTranslation();
  if (!salon) return null;

  const name = pickLocalized(salon.name, lang);
  const tagline = pickLocalized(salon.tagline, lang);
  const address = pickLocalized(salon.address, lang);
  const hours = pickLocalized(salon.workingHours, lang);

  return (
    <div
      style={{
        position: "relative",
        minHeight: "clamp(220px, 32vw, 320px)",
        backgroundImage: salon.coverImage
          ? `url(${salon.coverImage})`
          : palette.brandGradient,
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: "#fff",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: palette.heroOverlay,
        }}
      />
      <div
        className="container container-two"
        style={{
          position: "relative",
          zIndex: 1,
          paddingTop: "clamp(28px, 5vw, 60px)",
          paddingBottom: "clamp(24px, 4vw, 50px)",
        }}
      >
        <div className="d-flex flex-wrap align-items-end gap-3">
          {/* Logo medallion */}
          <span
            style={{
              width: "clamp(64px, 12vw, 88px)",
              height: "clamp(64px, 12vw, 88px)",
              borderRadius: 20,
              background: "rgba(255,255,255,0.94)",
              color: palette.accent,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "clamp(2rem, 5vw, 2.6rem)",
              boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
              flexShrink: 0,
            }}
            aria-hidden="true"
          >
            {salon.logoEmoji || "💇"}
          </span>

          {/* Salon info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              className="fw-700 mb-1"
              style={{
                fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
                lineHeight: 1.15,
                color: "#fff",
                textShadow: "0 2px 12px rgba(0,0,0,0.35)",
                wordBreak: "break-word",
              }}
            >
              {name}
            </h1>
            {tagline ? (
              <p
                className="mb-2"
                style={{
                  fontSize: "clamp(0.95rem, 2vw, 1.05rem)",
                  color: "rgba(255,255,255,0.92)",
                  textShadow: "0 1px 6px rgba(0,0,0,0.45)",
                  maxWidth: 620,
                }}
              >
                {tagline}
              </p>
            ) : null}

            <div className="d-flex flex-wrap align-items-center gap-3">
              {salon.rating ? (
                <span className="d-inline-flex align-items-center gap-1">
                  <i
                    className="las la-star"
                    style={{ color: "hsl(45 95% 60%)", fontSize: "1.15rem" }}
                    aria-hidden="true"
                  />
                  <strong style={{ fontSize: "0.95rem" }}>
                    {salon.rating.toFixed(2)}
                  </strong>
                  {salon.reviewsCount ? (
                    <span style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.85)" }}>
                      ({salon.reviewsCount} {t("salonBooking.hero.reviews")})
                    </span>
                  ) : null}
                </span>
              ) : null}
              {address ? (
                <span
                  className="d-inline-flex align-items-center gap-1"
                  style={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.92)" }}
                >
                  <i className="las la-map-marker-alt" aria-hidden="true" />
                  {address}
                </span>
              ) : null}
              {hours ? (
                <span
                  className="d-inline-flex align-items-center gap-1"
                  style={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.92)" }}
                >
                  <i className="las la-clock" aria-hidden="true" />
                  {hours}
                </span>
              ) : null}
              {salon.phone ? (
                <a
                  href={`tel:${salon.phone.replace(/\s/g, "")}`}
                  className="d-inline-flex align-items-center gap-1"
                  style={{
                    fontSize: "0.9rem",
                    color: "#fff",
                    textDecoration: "none",
                    background: "rgba(255,255,255,0.18)",
                    padding: "4px 10px",
                    borderRadius: 999,
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <i className="las la-phone" aria-hidden="true" />
                  {salon.phone}
                </a>
              ) : null}
            </div>

            <div
              className="d-flex flex-wrap align-items-center gap-2"
              style={{ marginTop: 14 }}
            >
              {(salon.valueProps || []).map((item) => (
                <span
                  key={item.en || item.hy}
                  className="d-inline-flex align-items-center gap-1"
                  style={{
                    background: "rgba(255,255,255,0.16)",
                    border: "1px solid rgba(255,255,255,0.24)",
                    color: "#fff",
                    borderRadius: 999,
                    padding: "6px 10px",
                    fontSize: "0.82rem",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <i className={item.icon || "las la-check"} aria-hidden="true" />
                  {pickLocalized(item, lang)}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div
          className="d-flex flex-wrap"
          style={{
            gap: 10,
            marginTop: "clamp(20px, 4vw, 34px)",
          }}
        >
          {[
            {
              value: salon.bookedToday ? `${salon.bookedToday}+` : null,
              label: t("salonBooking.hero.bookedToday"),
            },
            {
              value: salon.avgConfirmMinutes ? `${salon.avgConfirmMinutes} ${t("salonBooking.hero.minutes")}` : null,
              label: t("salonBooking.hero.confirmSpeed"),
            },
            {
              value: salon.repeatRate ? `${salon.repeatRate}%` : null,
              label: t("salonBooking.hero.repeatClients"),
            },
          ].filter((item) => item.value).map((item) => (
            <div
              key={item.label}
              style={{
                minWidth: 130,
                background: "rgba(255,255,255,0.14)",
                border: "1px solid rgba(255,255,255,0.22)",
                borderRadius: 14,
                padding: "10px 12px",
                backdropFilter: "blur(10px)",
              }}
            >
              <div className="fw-700" style={{ fontSize: "1.12rem", lineHeight: 1.1 }}>
                {item.value}
              </div>
              <div style={{ color: "rgba(255,255,255,0.82)", fontSize: "0.76rem", marginTop: 2 }}>
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
